import type { UserDetail, UserSummary } from '../../services/types';
import { EMPTY_VALUE } from '../../utils/dateFormat';

/**
 * Aufbereitung der mvgen-Benutzerstammdaten fuer die Anzeige
 * (Liste und Detailbereich teilen sich diese Regeln).
 */

/** Verbindet gefuellte Textteile mit einem Trennzeichen. */
function joinParts(parts: readonly (string | null)[], separator: string): string {
  return parts.filter((part): part is string => part !== null && part !== '').join(separator);
}

/**
 * Anzeigename eines Benutzers:
 * - juristische Person: Firmenname
 * - natuerliche Person: "Vorname Nachname"
 * Fehlen die Angaben (in mvgen optional), faellt die Anzeige auf den
 * Anmeldenamen zurueck — eine leere Namensspalte waere nicht anklickbar
 * erkennbar.
 */
export function formatUserDisplayName(user: UserSummary): string {
  if (user.isLegalPerson && user.companyName !== null) {
    return user.companyName;
  }
  const personName = joinParts([user.firstName, user.lastName], ' ');
  if (personName !== '') {
    return personName;
  }
  if (user.companyName !== null) {
    return user.companyName;
  }
  return user.username !== '' ? user.username : EMPTY_VALUE;
}

/**
 * Postanschrift als einzeiliger Text
 * ("Musterstraße 1, 12345 Musterstadt"); Gedankenstrich, wenn keine
 * Adressangabe vorliegt.
 */
export function formatUserAddress(user: UserDetail): string {
  const street = joinParts([user.street, user.houseNumber], ' ');
  const town = joinParts([user.postalCode, user.city], ' ');
  const address = joinParts([street, town], ', ');
  return address === '' ? EMPTY_VALUE : address;
}

/**
 * Deutsche Bezeichnungen der bekannten Anmeldearten.
 *
 * Bewusst eine Map und kein Objekt-Literal: Der Wert kommt aus der
 * mvgen-Datenbank. Bei einem Objekt lieferte der Zugriff mit "toString"
 * oder "constructor" einen geerbten Prototype-Wert (eine Funktion), der
 * als React-Child einen console.error ausloeste.
 */
const AUTH_TYPE_LABELS: ReadonlyMap<string, string> = new Map([
  ['LOCAL', 'Passwort'],
  ['GOOGLE', 'Google'],
  ['OAUTH2', 'OAuth2'],
]);

/**
 * Anmeldeart in deutscher Schreibweise; unbekannte Werte werden
 * unveraendert angezeigt, damit ein neuer mvgen-Wert auffaellt.
 */
export function formatAuthType(authType: string | null): string {
  if (authType === null || authType === '') {
    return EMPTY_VALUE;
  }
  const label: string | undefined = AUTH_TYPE_LABELS.get(authType);
  return label ?? authType;
}
